"use client";

import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Staggered parallax depths per tile (alternating near/far layers)
const TILE_PARALLAX_DEPTHS = [0, -45, -25, -60, -35];

function ParallaxTile({
  children,
  depth,
  className,
  onMouseEnter,
  onMouseLeave,
}: {
  children: React.ReactNode;
  depth: number;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [Math.abs(depth) * 0.4, depth]);

  return (
    <motion.div
      ref={ref}
      style={{ y }}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </motion.div>
  );
}

function UseCasesSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={`skeleton-card min-h-[200px] ${i === 0 ? 'lg:col-span-2' : ''}`}>
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="skeleton skeleton-icon" style={{ marginBottom: 0 }} />
              <div className="skeleton skeleton-line" style={{ width: '70px', height: '22px', marginBottom: 0 }} />
            </div>
            <div className="skeleton skeleton-line" style={{ width: '60px', height: '22px', marginBottom: 0 }} />
          </div>
          <div className="skeleton skeleton-line skeleton-line-medium mb-3" style={{ height: '18px' }} />
          <div className="skeleton skeleton-line skeleton-line-full" />
          <div className="skeleton skeleton-line skeleton-line-medium" />
        </div>
      ))}
    </div>
  );
}

export default function UseCasesSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.08 });
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const useCases = [
    { title: t('uc1_title'), desc: t('uc1_desc'), icon: '📊', tag: t('uc1_tag'), metric: '8hrs → 12min', metricLabel: t('uc1_metric_label') },
    { title: t('uc2_title'), desc: t('uc2_desc'), icon: '⚖️', tag: t('uc2_tag'), metric: '100%', metricLabel: t('uc2_metric_label') },
    { title: t('uc3_title'), desc: t('uc3_desc'), icon: '🎯', tag: t('uc3_tag'), metric: '3×', metricLabel: t('uc3_metric_label') },
    { title: t('uc4_title'), desc: t('uc4_desc'), icon: '⚙️', tag: t('uc4_tag'), metric: '60%', metricLabel: t('uc4_metric_label') },
    { title: t('uc5_title'), desc: t('uc5_desc'), icon: '💬', tag: t('uc5_tag'), metric: '24/7', metricLabel: t('uc5_metric_label') },
  ];

  useEffect(() => {
    const loadTimer = setTimeout(() => setLoaded(true), 700);
    return () => clearTimeout(loadTimer);
  }, []);

  return (
    <section id="use-cases" ref={sectionRef} className="py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(139,158,192,0.15)] to-transparent" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,145,46,0.04) 0%, transparent 70%)' }} />
      <div className="max-w-container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={0}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="section-label mb-4">{t('usecases_label')}</div>
          <h2 className="font-heading text-headline font-bold text-white mb-5">
            {t('usecases_headline_1')}{' '}
            <span className="text-gradient-orange">{t('usecases_headline_accent')}</span>
          </h2>
          <p className="text-muted text-base sm:text-lg max-w-xl mx-auto">{t('usecases_sub')}</p>
        </motion.div>

        {!loaded ? (
          <UseCasesSkeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((uc, i) => (
              <ParallaxTile
                key={uc.title}
                depth={TILE_PARALLAX_DEPTHS[i]}
                className={i === 0 ? 'lg:col-span-2' : ''}
                onMouseEnter={() => setActiveCard(i)}
                onMouseLeave={() => setActiveCard(null)}
              >
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  animate={inView ? 'visible' : 'hidden'}
                  custom={i + 1}
                >
                  <div
                    className={`card-glass use-case-tile rounded-3xl p-6 sm:p-7 h-full relative overflow-hidden cursor-pointer border ${
                      activeCard === i ? 'border-[rgba(255,145,46,0.4)]' : 'border-[rgba(139,158,192,0.15)]'
                    }`}
                  >
                    <div
                      className="absolute inset-0 transition-opacity duration-500 pointer-events-none"
                      style={{
                        background: 'radial-gradient(circle at 25% 25%, rgba(255,145,46,0.1) 0%, transparent 65%)',
                        opacity: activeCard === i ? 1 : 0,
                      }}
                    />
                    <div
                      className="absolute top-0 left-0 right-0 h-px transition-opacity duration-400 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,145,46,0.6), transparent)',
                        opacity: activeCard === i ? 1 : 0,
                      }}
                    />
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-[rgba(255,145,46,0.1)] border flex items-center justify-center text-2xl transition-all duration-400 ${
                            activeCard === i ? 'border-[rgba(255,145,46,0.45)] bg-[rgba(255,145,46,0.2)] shadow-[0_0_18px_rgba(255,145,46,0.35)]' : 'border-[rgba(255,145,46,0.15)]'
                          }`}>
                            {uc.icon}
                          </div>
                          <span className="px-3 py-1 rounded-full bg-[rgba(139,158,192,0.08)] border border-[rgba(139,158,192,0.15)] text-[11px] font-bold text-muted uppercase tracking-widest">
                            {uc.tag}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-heading text-xl font-bold text-primary">{uc.metric}</div>
                          <div className="text-[10px] text-muted">{uc.metricLabel}</div>
                        </div>
                      </div>
                      <h3 className="font-heading text-lg sm:text-xl font-bold text-white mb-3">{uc.title}</h3>
                      <p className="text-muted text-sm leading-relaxed">{uc.desc}</p>
                      <div
                        className="mt-5 flex items-center gap-2 text-primary text-sm font-semibold transition-all duration-350"
                        style={{
                          opacity: activeCard === i ? 1 : 0,
                          transform: activeCard === i ? 'translateX(0)' : 'translateX(-10px)',
                        }}
                      >
                        <span>{t('usecases_see_how')}</span>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </ParallaxTile>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}